import { toast } from "sonner";
import { Bell, Sparkles } from "lucide-react";
import { criteriaSummary, type AlertCriteria, type AlertStatus } from "@avestate/shared";
import { useAlerts, usePatchAlert, type Alert } from "@/hooks/useAlerts.js";
import { Button } from "@/components/ui/button.js";
import { Badge } from "@/components/ui/badge.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { RoleGate } from "@/components/RoleGate.js";
import { fmtDateTime } from "@/lib/format.js";

const STATUS: Record<AlertStatus, { tone: "success" | "warn" | "neutral"; label: string }> = {
  active: { tone: "success", label: "attiva" },
  pending: { tone: "warn", label: "in attesa" },
  paused: { tone: "neutral", label: "in pausa" },
  unsubscribed: { tone: "neutral", label: "annullata" },
};

export default function Alerts(): JSX.Element {
  const { data, isLoading } = useAlerts();
  const patch = usePatchAlert();
  const items = data?.items ?? [];
  const active = items.filter((a) => a.status === "active").length;
  const acquisition = items.filter((a) => a.status === "active" && a.match_count === 0).length;
  const pendingId = patch.isPending ? patch.variables?.id ?? null : null;

  async function toggle(a: Alert) {
    const next: AlertStatus = a.status === "active" ? "paused" : "active";
    try { await patch.mutateAsync({ id: a.id, status: next }); toast.success(next === "active" ? "Riattivato" : "In pausa"); }
    catch (e) { toast.error((e as Error).message); }
  }

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Alert acquirenti</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">
          Ricerche salvate dai visitatori. Quando pubblichi un immobile che corrisponde, l'acquirente e l'agenzia ricevono un avviso.
        </p>
      </header>

      <div className="grid grid-cols-3 overflow-hidden rounded-[5px] border border-rule bg-surface">
        <Stat v={String(items.length)} l="Ricerche salvate" />
        <Stat v={String(active)} l="Attive" />
        <Stat v={String(acquisition)} l="Cercano, 0 match" last />
      </div>

      {acquisition > 0 ? (
        <div className="rounded-[5px] border border-accent/30 bg-accent/5 p-[12px_16px] text-[12.5px] text-ink">
          <Sparkles className="mr-1.5 inline size-4 text-accent" />
          {acquisition} acquirenti cercano qualcosa che non hai ancora in catalogo: sono lead di acquisizione.
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-[5px] border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Acquirente</TableHeader>
              <TableHeader>Ricerca</TableHeader>
              <TableHeader className="w-[90px] text-right">Match</TableHeader>
              <TableHeader className="w-[100px]">Stato</TableHeader>
              <TableHeader className="w-[150px]">Ultimo avviso</TableHeader>
              <TableHeader className="w-[120px] text-right">Azioni</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((a) => {
              const lead = a.status === "active" && a.match_count === 0;
              return (
                <TableRow key={a.id} className={lead ? "bg-accent/[0.03]" : undefined}>
                  <TableCell className="text-[13px] text-ink">{a.email}</TableCell>
                  <TableCell className="text-[13px] text-ink-soft">
                    {criteriaSummary(a.criteria as AlertCriteria)}
                    {lead ? <span className="ml-2 rounded-pill border border-accent/40 px-1.5 py-0.5 text-[10.5px] uppercase tracking-wider text-accent">da trovare</span> : null}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[13px] text-ink">{a.match_count}</TableCell>
                  <TableCell><Badge tone={STATUS[a.status].tone}>{STATUS[a.status].label}</Badge></TableCell>
                  <TableCell className="font-mono text-meta text-ink-mute">{a.last_notified_at ? fmtDateTime(a.last_notified_at) : "—"}</TableCell>
                  <TableCell className="text-right">
                    {a.status === "active" || a.status === "paused" ? (
                      <RoleGate min="editor">
                        <Button size="sm" variant="outline" disabled={pendingId !== null}
                          aria-label={`${a.status === "active" ? "Metti in pausa" : "Riattiva"} la ricerca di ${a.email}`}
                          onClick={() => void toggle(a)}>
                          {pendingId === a.id ? "…" : a.status === "active" ? "Pausa" : "Riattiva"}
                        </Button>
                      </RoleGate>
                    ) : <span className="text-ink-mute">—</span>}
                  </TableCell>
                </TableRow>
              );
            })}
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-ink-mute">Caricamento ricerche…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-ink-mute">
                <Bell className="mx-auto mb-2 size-5 text-ink-mute" />
                Nessuna ricerca salvata. Compare qui quando un visitatore salva una ricerca su /vendita.
              </TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Stat({ v, l, last }: { v: string; l: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}</div>
      <div className="micro mt-[5px]">{l}</div>
    </div>
  );
}
